/** Admin blog editor — zadání článku, AI generování, úpravy, publikace. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Sparkle, FloppyDisk, Globe, Trash, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { BlogWidget } from "../components/blog/BlogWidgets";
import { blogGenerate, blogGenerateData, blogGenerateClub, blogAdminList, blogAdminGet, blogUpdate, blogDelete, blogDailyRun } from "../lib/blog-api";
import BlogInterview from "../components/blog/BlogInterview";
import { findPlayers } from "../lib/lab-api";
import { timingSearchPlayers } from "../lib/timing-api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PollEditor({ post, onChanged }) {
    const [question, setQuestion] = useState(post.poll?.question || "");
    const [optionsText, setOptionsText] = useState((post.poll?.options || []).join("\n"));
    const [correct, setCorrect] = useState(0);
    const poll = post.poll;

    const save = async () => {
        const options = optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
        try {
            await axios.put(`${API}/blog/posts/${post.id}/poll`, { question, options });
            toast.success("Anketa uložena");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení ankety selhalo"); }
    };
    const resolve = async () => {
        try {
            const r = await axios.post(`${API}/blog/posts/${post.id}/poll/resolve`, { correct_option: Number(correct) });
            toast.success(`Vyhodnoceno — reputace přidělena ${r.data.awarded_users} manažerům`);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Vyhodnocení selhalo"); }
    };
    const remove = async () => {
        try { await axios.delete(`${API}/blog/posts/${post.id}/poll`); toast.success("Anketa smazána"); onChanged(); }
        catch { toast.error("Smazání selhalo"); }
    };

    return (
        <div className="border border-fa-yellow/30 bg-fa-yellow/5 p-3 space-y-2" data-testid="poll-editor">
            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow">
                Anketa pod článkem {poll?.status === "resolved" ? "· VYHODNOCENO" : poll ? "· otevřená" : ""}
            </div>
            <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Otázka (např. Koupil bys Salaha za 14.5?)"
                   disabled={poll?.status === "resolved"}
                   className="w-full bg-black/40 border border-white/15 px-2.5 py-2 font-mono text-[11px] focus:outline-none focus:border-fa-yellow" data-testid="poll-question-input" />
            <textarea value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={3}
                      disabled={poll?.status === "resolved"}
                      placeholder={"Možnosti — jedna na řádek (2–6)\nAno, hned\nPočkat 1 GW\nNe"}
                      className="w-full bg-black/40 border border-white/15 px-2.5 py-2 font-mono text-[10px] focus:outline-none focus:border-fa-yellow" data-testid="poll-options-input" />
            <div className="flex flex-wrap gap-2 items-center">
                {poll?.status !== "resolved" && (
                    <button type="button" onClick={save} data-testid="poll-save-btn"
                            className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 bg-fa-yellow text-black font-bold">
                        {poll ? "Upravit anketu" : "Vytvořit anketu"}
                    </button>
                )}
                {poll && poll.status !== "resolved" && (
                    <>
                        <select value={correct} onChange={(e) => setCorrect(e.target.value)} data-testid="poll-correct-select"
                                className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px]">
                            {(poll.options || []).map((o, i) => <option key={i} value={i}>{o}</option>)}
                        </select>
                        <button type="button" onClick={resolve} data-testid="poll-resolve-btn"
                                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                            Vyhodnotit (+5 rep správným)
                        </button>
                    </>
                )}
                {poll && (
                    <button type="button" onClick={remove} data-testid="poll-delete-btn"
                            className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-white/15 text-white/40 hover:text-red-400">
                        Smazat
                    </button>
                )}
            </div>
        </div>
    );
}

const STATUS_STYLE = {
    generating: "text-fa-yellow", draft: "text-fa-sky", published: "text-fa-mint", failed: "text-red-300",
};
const STATUS_LABEL = { generating: "generuje se…", draft: "koncept", published: "publikováno", failed: "chyba" };

export default function BlogAdmin() {
    const [posts, setPosts] = useState([]);
    const [query, setQuery] = useState("");
    const [playerResults, setPlayerResults] = useState([]);
    const [player, setPlayer] = useState(null);
    const [theses, setTheses] = useState("");
    const [busy, setBusy] = useState(false);
    const [dataQuery, setDataQuery] = useState("");
    const [dataResults, setDataResults] = useState([]);
    const [dataPlayers, setDataPlayers] = useState([]);
    const [dataTopic, setDataTopic] = useState("10 hráčů, které trh zatím podceňuje");
    const [dataNote, setDataNote] = useState("");
    const [current, setCurrent] = useState(null);
    const [preview, setPreview] = useState(false);
    const [tweetBusy, setTweetBusy] = useState(false);
    const pollRef = useRef(null);

    const generateTweet = async () => {
        if (!current?.id) return;
        setTweetBusy(true);
        try {
            const r = await axios.post(`${API}/blog/admin/posts/${current.id}/share-text`, {}, { withCredentials: true, timeout: 90000 });
            setCurrent((c) => ({ ...c, share_text_x: r.data.text }));
            toast.success("Tweet vygenerován — dolaď ho a ulož");
        } catch (e) { toast.error(e.response?.data?.detail || "Generování tweetu selhalo"); }
        finally { setTweetBusy(false); }
    };

    const shareTweet = () => {
        if (!current?.slug) { toast.error("Článek nemá slug — nejdřív ulož"); return; }
        const text = current.share_text_x || `${current.title} — ${current.perex || ""}`.slice(0, 220);
        const url = `${window.location.origin}/blog/${current.slug}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank");
    };

    const loadPosts = () => blogAdminList().then((d) => setPosts(d.posts)).catch(() => toast.error("Nemáš admin přístup nebo se nepodařilo načíst"));
    useEffect(() => { loadPosts(); return () => clearTimeout(pollRef.current); }, []);

    useEffect(() => {
        if (!query.trim()) { setPlayerResults([]); return; }
        const t = setTimeout(() => {
            timingSearchPlayers({ q: query, limit: 8 }).then((d) => setPlayerResults(d.players || [])).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!dataQuery.trim()) { setDataResults([]); return; }
        const t = setTimeout(() => {
            findPlayers({ search: dataQuery, limit: 8 }).then((d) => setDataResults(d.results || [])).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [dataQuery]);

    const pollUntilDone = (id) => {
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(async () => {
            try {
                const p = await blogAdminGet(id);
                if (p.status === "generating" || p.hero_status === "generating") { setCurrent(p); pollUntilDone(id); return; }
                setCurrent(p);
                loadPosts();
                if (p.status === "failed") toast.error(`Generování selhalo: ${p.error || ""}`);
                else if (p.hero_status === "failed") toast.error("AI obrázek se nepodařil — zkus znovu");
                else toast.success("Hotovo — článek i obrázek připraveny");
            } catch { pollUntilDone(id); }
        }, 4000);
    };

    const generate = async () => {
        if (!player) { toast.error("Vyber hráče"); return; }
        setBusy(true);
        try {
            const r = await blogGenerate({ player_key: player.player_key, theses });
            toast.info("Generuji článek (~30-60 s)…");
            setCurrent({ id: r.id, status: "generating", player_name: player.display_name });
            loadPosts();
            pollUntilDone(r.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Generování selhalo"); }
        finally { setBusy(false); }
    };

    const [clubList, setClubList] = useState([]);
    const [clubKey, setClubKey] = useState("");
    const [clubNote, setClubNote] = useState("");

    useEffect(() => {
        axios.get(`${API}/preseason-db/overview`, { timeout: 30000 }).then((r) => {
            const seen = {};
            (r.data.matches || []).forEach((m) => { if (m.club_key) seen[m.club_key] = m.club; });
            setClubList(Object.entries(seen).map(([k, v]) => ({ key: k, name: v })).sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(() => {});
    }, []);

    const generateClub = async () => {
        if (!clubKey) { toast.error("Vyber klub"); return; }
        setBusy(true);
        try {
            const r = await blogGenerateClub({ club_key: clubKey, note: clubNote });
            toast.info("Generuji Club Opportunities článek (~60-90 s)…");
            setCurrent({ id: r.id, status: "generating" });
            loadPosts();
            pollUntilDone(r.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Generování selhalo"); }
        finally { setBusy(false); }
    };

    const generateData = async () => {
        setBusy(true);
        try {
            const r = await blogGenerateData({
                topic: dataTopic,
                note: dataNote,
                fpl_ids: dataPlayers.map((p) => p.fpl_id),
            });
            toast.info("Generuji datový článek (~30-60 s)…");
            setCurrent({ id: r.id, status: "generating" });
            loadPosts();
            pollUntilDone(r.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Generování selhalo"); }
        finally { setBusy(false); }
    };

    const save = async (extra = {}) => {
        if (!current?.id) return;
        try {
            const p = await blogUpdate(current.id, {
                title: current.title, perex: current.perex, content_md: current.content_md,
                seo_description: current.seo_description, tags: current.tags,
                category: current.category || "players", share_text_x: current.share_text_x || null, ...extra,
            });
            setCurrent(p);
            loadPosts();
            toast.success(extra.status === "published" ? "Publikováno!" : "Uloženo");
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const remove = async (id) => {
        await blogDelete(id).catch(() => {});
        if (current?.id === id) setCurrent(null);
        loadPosts();
        toast.success("Smazáno");
    };

    const openPost = async (id) => {
        const p = await blogAdminGet(id);
        setCurrent(p);
        if (p.status === "generating") pollUntilDone(id);
    };

    const set = (k) => (e) => setCurrent({ ...current, [k]: e.target?.value ?? e });

    const readFile = (file) => new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result);
        rd.onerror = rej;
        rd.readAsDataURL(file);
    });

    const uploadHero = async (file) => {
        if (!file || !current?.id) return;
        try {
            const b64 = await readFile(file);
            await axios.post(`${API}/blog/posts/${current.id}/hero/upload`, { image_base64: b64 }, { withCredentials: true });
            setCurrent({ ...current, hero_status: "ready", updated_at: new Date().toISOString() });
            toast.success("Vlastní hero obrázek nahrán");
        } catch (e) { toast.error(e.response?.data?.detail || "Nahrání selhalo"); }
    };

    const uploadInline = async (file) => {
        if (!file || !current) return;
        try {
            const b64 = await readFile(file);
            const alt = file.name.replace(/\.[a-z]+$/i, "").replace(/[-_]/g, " ");
            const r = await axios.post(`${API}/blog/images/upload`, { image_base64: b64, alt }, { withCredentials: true });
            setCurrent({ ...current, content_md: `${current.content_md || ""}\n\n${r.data.markdown}\n` });
            toast.success("Obrázek vložen na konec článku — přesuň markdown kam potřebuješ a ulož");
        } catch (e) { toast.error(e.response?.data?.detail || "Nahrání selhalo"); }
    };

    const exportPost = async () => {
        if (!current?.id) return;
        try {
            const r = await axios.get(`${API}/blog/posts/${current.id}/export`, { withCredentials: true });
            const blob = new Blob([JSON.stringify(r.data)], { type: "application/json" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `${current.slug || current.id}.fa-article.json`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success("Článek exportován — soubor nahraješ na livu přes Import");
        } catch (e) { toast.error(e.response?.data?.detail || "Export selhal"); }
    };

    const importPost = async (file) => {
        if (!file) return;
        try {
            const data = JSON.parse(await file.text());
            const r = await axios.post(`${API}/blog/import`, data, { withCredentials: true });
            toast.success(`Naimportováno: ${r.data.slug} (${r.data.status === "published" ? "publikováno" : "koncept"}${r.data.replaced_existing ? ", přepsán existující" : ""})`);
            loadPosts();
            openPost(r.data.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Import selhal — je to platný .fa-article.json soubor?"); }
    };

    const runDaily = async () => {
        setBusy(true);
        try {
            const d = await blogDailyRun();
            if (d.status === "skipped") toast.info(d.detail);
            else { toast.success(`Denní přehled pro ${d.date} vygenerován a publikován`); loadPosts(); }
        } catch (e) { toast.error(e.response?.data?.detail || "Generování selhalo"); }
        finally { setBusy(false); }
    };

    const mdParts = useMemo(() => (current?.content_md || "").split(/\[\[widget:([a-z0-9_]+)\]\]/g), [current?.content_md]);

    return (
        <AppShell>
            <div className="max-w-6xl mx-auto space-y-5" data-testid="blog-admin-page">
                <header>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream">Admin</div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">Blog editor</h1>
                </header>

                {/* Generation form */}
                <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3" data-testid="blog-gen-form">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Nový článek — AI generování z dat</p>
                    <div className="flex gap-2 flex-wrap items-start">
                        <div className="relative">
                            <input value={player ? player.display_name : query}
                                   onChange={(e) => { setPlayer(null); setQuery(e.target.value); }}
                                   placeholder="Hráč — Salah, Haaland…"
                                   className="bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] w-64 focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                                   data-testid="blog-player-search" />
                            {!player && playerResults.length > 0 && (
                                <div className="absolute z-20 top-full left-0 w-64 bg-[#151515] border border-white/15 max-h-56 overflow-auto">
                                    {playerResults.map((p) => (
                                        <button key={p.player_key} type="button" data-testid={`blog-player-${p.player_key}`}
                                                onClick={() => { setPlayer(p); setQuery(""); setPlayerResults([]); }}
                                                className="block w-full text-left px-3 py-1.5 font-mono text-[10px] text-white/70 hover:bg-white/5">
                                            {p.display_name} <span className="text-white/35">{(p.positions || []).join("/")}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <textarea value={theses} onChange={(e) => setTheses(e.target.value)} rows={3}
                                  placeholder="Tvoje teze — např.: 'Myslím, že je to nejlepší budget pick na start sezóny. Doma je jiný hráč než venku. Pozor na těžký listopad.'"
                                  className="bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] flex-1 min-w-[300px] focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                                  data-testid="blog-theses" />
                        <button type="button" onClick={generate} disabled={busy || !player} data-testid="blog-generate-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 bg-fa-mint text-black font-bold hover:opacity-90 disabled:opacity-40">
                            <Sparkle size={12} weight="fill" className="inline mb-0.5 mr-1" /> Vygenerovat
                        </button>
                    </div>
                </div>

                {/* Datový článek — vlastní výběr hráčů + research */}
                <div className="border border-fa-sky/30 bg-fa-sky/5 p-4 space-y-2" data-testid="blog-data-panel">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-fa-sky">Datový článek — vyber hráče a přilož vlastní research (nebo nech prázdné = model vybere 10 podceněných)</p>
                    <input value={dataTopic} onChange={(e) => setDataTopic(e.target.value)} data-testid="blog-data-topic"
                           placeholder="Téma / titulek článku"
                           className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px]" />
                    <div className="relative">
                        <input value={dataQuery} onChange={(e) => setDataQuery(e.target.value)} data-testid="blog-data-search"
                               placeholder="Hledat hráče (aktuální databáze 26/27)…"
                               className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px]" />
                        {dataResults.length > 0 && dataQuery && (
                            <div className="absolute z-20 left-0 right-0 top-full bg-[#111] border border-white/15 max-h-56 overflow-y-auto">
                                {dataResults.map((p) => (
                                    <button key={p.fpl_id} type="button" data-testid={`blog-data-result-${p.fpl_id}`}
                                            onClick={() => {
                                                if (!dataPlayers.find((x) => x.fpl_id === p.fpl_id) && dataPlayers.length < 15)
                                                    setDataPlayers([...dataPlayers, p]);
                                                setDataQuery(""); setDataResults([]);
                                            }}
                                            className="w-full text-left px-3 py-1.5 font-mono text-[11px] hover:bg-white/5">
                                        <span className="font-bold">{p.name}</span>
                                        <span className="text-white/45 ml-2">{p.team} · {p.position} · £{p.price}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {dataPlayers.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="blog-data-chips">
                            {dataPlayers.map((p, i) => (
                                <span key={p.fpl_id} className="font-mono text-[10px] border border-fa-sky/50 text-fa-sky px-2 py-1 flex items-center gap-1.5">
                                    {i + 1}. {p.name}
                                    <button type="button" onClick={() => setDataPlayers(dataPlayers.filter((x) => x.fpl_id !== p.fpl_id))}
                                            className="text-white/40 hover:text-red-400">×</button>
                                </span>
                            ))}
                        </div>
                    )}
                    <textarea value={dataNote} onChange={(e) => setDataNote(e.target.value)} rows={3} data-testid="blog-data-note"
                              placeholder="Tvůj research / postřehy k hráčům — AI je použije jako hlavní argumenty a doplní čísly z modelu"
                              className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] resize-y" />
                    <button type="button" onClick={generateData} disabled={busy} data-testid="blog-generate-data-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40">
                        <Sparkle size={12} className="inline mb-0.5 mr-1" />
                        {dataPlayers.length ? `Vygenerovat článek z ${dataPlayers.length} hráčů` : "Vygenerovat: 10 podceněných (auto výběr)"}
                    </button>
                </div>

                <BlogInterview onArticleCreated={loadPosts} />

                {/* Club Opportunities — seriálový formát po klubech */}
                <div className="border border-fa-mint/30 bg-fa-mint/5 p-4 space-y-2" data-testid="blog-club-panel">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">Club Opportunities — seriál: taktika → příležitosti → rotace → tier list (data ze scoutingu přípravy + model)</p>
                    <div className="flex gap-2 flex-wrap items-center">
                        <select value={clubKey} onChange={(e) => setClubKey(e.target.value)} data-testid="blog-club-select"
                                className="bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] min-w-[220px]">
                            <option value="">— vyber klub (jen kluby se scouting daty) —</option>
                            {clubList.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                        </select>
                        <button type="button" onClick={generateClub} disabled={busy || !clubKey} data-testid="blog-generate-club-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-40">
                            <Sparkle size={12} className="inline mb-0.5 mr-1" /> Vygenerovat Club Opportunities
                        </button>
                    </div>
                    <textarea value={clubNote} onChange={(e) => setClubNote(e.target.value)} rows={2} data-testid="blog-club-note"
                              placeholder="Volitelný research / hlavní teze ke klubu — AI je použije jako hlavní argumentační linku"
                              className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] resize-y" />
                </div>

                <div className="flex items-center gap-2">
                    <button type="button" onClick={runDaily} disabled={busy} data-testid="blog-daily-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/25 text-white/70 hover:text-white hover:border-white/50 disabled:opacity-40">
                        ⟳ Vygenerovat denní přehled (včerejšek)
                    </button>
                    <span className="font-mono text-[9px] text-white/35">Denní přehled se generuje POUZE ručně tímto tlačítkem — automat je vypnutý</span>
                    <label data-testid="blog-import-btn"
                           className="ml-auto font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black cursor-pointer">
                        ⬆ Import článku (.fa-article.json)
                        <input type="file" accept=".json,application/json" className="hidden"
                               onChange={(e) => { importPost(e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                </div>


                <div className="grid lg:grid-cols-[280px_1fr] gap-4">
                    {/* Post list */}
                    <div className="space-y-1.5" data-testid="blog-admin-list">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Články ({posts.length})</p>
                        {posts.map((p) => (
                            <div key={p.id} className={`border p-2.5 cursor-pointer ${current?.id === p.id ? "border-fa-mint/60 bg-fa-mint/5" : "border-white/10 bg-[#0d0d0d] hover:border-white/25"}`}
                                 onClick={() => openPost(p.id)} data-testid={`blog-admin-item-${p.id}`}>
                                <div className="font-mono text-[8px] uppercase tracking-widest flex justify-between">
                                    <span className={STATUS_STYLE[p.status]}>{STATUS_LABEL[p.status]}</span>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="text-red-300/60 hover:text-red-300"><Trash size={11} /></button>
                                </div>
                                <div className="font-display font-bold text-sm mt-0.5">{p.title || p.player_name || p.player_key}</div>
                                <div className="font-mono text-[8px] text-white/35">{new Date(p.created_at).toLocaleString("cs")}</div>
                            </div>
                        ))}
                    </div>

                    {/* Editor */}
                    <div className="space-y-3">
                        {!current && <p className="font-mono text-[11px] text-white/40 border border-white/10 bg-[#0d0d0d] p-6">Vyber článek vlevo, nebo vygeneruj nový.</p>}
                        {current?.status === "generating" && (
                            <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-4 font-mono text-[11px] text-fa-yellow flex items-center gap-2" data-testid="blog-generating">
                                <ArrowsClockwise size={13} className="animate-spin" /> GPT píše analýzu z dat… (~30-60 s)
                            </div>
                        )}
                        {current && current.status !== "generating" && (
                            <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3" data-testid="blog-editor">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`font-mono text-[9px] uppercase tracking-widest ${STATUS_STYLE[current.status]}`}>{STATUS_LABEL[current.status]}</span>
                                    {current.slug && current.status === "published" && (
                                        <a href={`/blog/${current.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[9px] text-fa-sky underline">/blog/{current.slug}</a>
                                    )}
                                    <button type="button" onClick={() => setPreview(!preview)} data-testid="blog-preview-toggle"
                                            className="ml-auto font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-white/20 text-white/70 hover:border-fa-sky">
                                        <Eye size={11} className="inline mb-0.5 mr-1" /> {preview ? "Editor" : "Náhled"}
                                    </button>
                                    <button type="button" data-testid="blog-hero-btn"
                                            disabled={current.hero_status === "generating"}
                                            onClick={async () => {
                                                try {
                                                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${current.id}/hero`, {}, { withCredentials: true });
                                                    setCurrent({ ...current, hero_status: "generating" });
                                                    toast.info("Generuji AI obrázek (~1 min)…");
                                                    pollUntilDone(current.id);
                                                } catch (e) { toast.error(e.response?.data?.detail || "Generování obrázku selhalo"); }
                                            }}
                                            className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black disabled:opacity-40">
                                        {current.hero_status === "generating" ? "Obrázek se generuje…" : current.hero_status === "ready" ? "🎨 Nový AI obrázek" : "🎨 AI obrázek"}
                                    </button>
                                    <label data-testid="blog-hero-upload"
                                           className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-violet/60 text-fa-cream hover:bg-fa-violet cursor-pointer">
                                        📁 Vlastní hero
                                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                                               onChange={(e) => { uploadHero(e.target.files?.[0]); e.target.value = ""; }} />
                                    </label>
                                    <label data-testid="blog-inline-image-upload"
                                           className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black cursor-pointer">
                                        🖼 Obrázek do textu
                                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                                               onChange={(e) => { uploadInline(e.target.files?.[0]); e.target.value = ""; }} />
                                    </label>
                                    <button type="button" onClick={exportPost} data-testid="blog-export-btn"
                                            className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-white/25 text-white/60 hover:text-white hover:border-white/50">
                                        ⬇ Export
                                    </button>
                                    <button type="button" onClick={() => save()} data-testid="blog-save-btn"
                                            className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black">
                                        <FloppyDisk size={11} className="inline mb-0.5 mr-1" /> Uložit
                                    </button>
                                    {current.status !== "published" ? (
                                        <button type="button" onClick={() => save({ status: "published" })} data-testid="blog-publish-btn"
                                                className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 bg-fa-mint text-black font-bold hover:opacity-90">
                                            <Globe size={11} className="inline mb-0.5 mr-1" /> Publikovat
                                        </button>
                                    ) : (
                                        <button type="button" onClick={() => save({ status: "draft" })} data-testid="blog-unpublish-btn"
                                                className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow hover:text-black">
                                            Stáhnout z webu
                                        </button>
                                    )}
                                </div>
                                {current.error && <p className="font-mono text-[10px] text-red-300">{current.error}</p>}
                                {current.hero_status === "ready" && current.slug && (
                                    <div data-testid="blog-hero-preview">
                                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mb-1">Hero obrázek článku</p>
                                        <img src={`${API}/blog/hero/${current.slug}.png?v=${encodeURIComponent(current.updated_at || "")}`}
                                             alt="Hero" className="w-full max-w-xl border border-white/15" />
                                    </div>
                                )}
                                {!preview ? (
                                    <>
                                        <input value={current.title || ""} onChange={set("title")} placeholder="Titulek"
                                               className="w-full bg-black/40 border border-white/15 px-3 py-2 font-display font-bold text-lg focus:outline-none focus:border-fa-mint" data-testid="blog-title-input" />
                                        <textarea value={current.perex || ""} onChange={set("perex")} rows={2} placeholder="Perex"
                                                  className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] focus:outline-none focus:border-fa-mint" data-testid="blog-perex-input" />
                                        <textarea value={current.seo_description || ""} onChange={set("seo_description")} rows={1} placeholder="SEO description (max 155 znaků)"
                                                  className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[10px] text-white/60 focus:outline-none focus:border-fa-mint" data-testid="blog-seo-input" />
                                        <div className="border border-white/10 bg-black/30 p-3 space-y-2" data-testid="blog-tweet-panel">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">𝕏 Tweet ke sdílení</span>
                                                <span className={`ml-auto font-mono text-[9px] ${(current.share_text_x || "").length > 1100 ? "text-red-300" : "text-white/40"}`} data-testid="blog-tweet-charcount">
                                                    {(current.share_text_x || "").length} znaků
                                                </span>
                                            </div>
                                            <textarea value={current.share_text_x || ""} onChange={set("share_text_x")} rows={8}
                                                      placeholder="Zatím žádný tweet — vygeneruj ho AI, nebo napiš vlastní. Odkaz na článek se při sdílení přidá automaticky."
                                                      className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] leading-relaxed focus:outline-none focus:border-fa-mint" data-testid="blog-tweet-input" />
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button type="button" onClick={generateTweet} disabled={tweetBusy} data-testid="blog-tweet-generate-btn"
                                                        className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40">
                                                    {tweetBusy ? "Generuji tweet…" : "✨ Vygenerovat AI tweet"}
                                                </button>
                                                <button type="button" onClick={shareTweet} data-testid="blog-tweet-share-btn"
                                                        className="font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border border-white/25 text-white/70 hover:border-white hover:text-white">
                                                    𝕏 Sdílet na X
                                                </button>
                                                <span className="font-mono text-[8px] text-white/30">Ruční úpravy ulož tlačítkem Uložit — stejný text pak použije i veřejné tlačítko Sdílet</span>
                                            </div>
                                        </div>
                                        <label className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-white/40">
                                            Kategorie (Academy)
                                            <select value={current.category || "players"} onChange={set("category")} data-testid="blog-category-select"
                                                    className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none focus:border-fa-mint">
                                                <option value="strategy">Strategy</option>
                                                <option value="players">Players</option>
                                                <option value="data_lab">Data Lab</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="guides">Guides</option>
                                            </select>
                                        </label>
                                        <PollEditor key={`${current.id}-${current.poll?.status || "none"}-${(current.poll?.options || []).length}`}
                                                    post={current}
                                                    onChanged={async () => { try { const p = await blogAdminGet(current.id); setCurrent(p); } catch { /* ignore */ } }} />
                                        <textarea value={current.content_md || ""} onChange={set("content_md")} rows={24} placeholder="Obsah (Markdown; widgety: [[widget:gw_chart]] [[widget:fdr_buckets]] [[widget:phase_breakdown]] [[widget:opponents]])"
                                                  className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] leading-relaxed focus:outline-none focus:border-fa-mint" data-testid="blog-content-input" />
                                    </>
                                ) : (
                                    <div className="max-w-3xl" data-testid="blog-preview">
                                        <h1 className="font-display font-black text-2xl uppercase tracking-tight mb-2">{current.title}</h1>
                                        <p className="text-white/60 italic border-l-2 border-fa-mint pl-3 mb-4">{current.perex}</p>
                                        {mdParts.map((part, i) => i % 2 === 1
                                            ? <BlogWidget key={i} name={part} widgets={current.widgets} />
                                            : <ReactMarkdown key={i} components={{
                                                h2: (p) => <h2 className="font-display font-black text-lg uppercase mt-6 mb-2 text-fa-cream" {...p} />,
                                                p: (p) => <p className="text-white/75 leading-relaxed mb-3 text-sm" {...p} />,
                                                li: (p) => <li className="text-white/75 ml-5 list-disc mb-1 text-sm" {...p} />,
                                                strong: (p) => <strong className="text-white font-bold" {...p} />,
                                                img: (p) => <img className="w-full border border-white/10 my-4" alt={p.alt || ""} {...p} />,
                                            }}>{part}</ReactMarkdown>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
